import { useState, useEffect, useMemo } from 'react';
import { Drawer, Box, Typography, IconButton, Button, TextField, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, FormHelperText } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { X } from 'lucide-react';
import { useAuthStore } from '@diggens/user-management';
import { usePicklists } from '@diggens/shared-hooks';
import { authenticatedFetch } from '@diggens/api-client';
import type { User } from '@diggens/types';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  user?: User; // If provided, edit mode; otherwise create mode
}

const ALL_ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Tenant Admin' },
  { value: 'billing_admin', label: 'Billing Admin' },
  { value: 'external_user', label: 'External User' },
  { value: 'user', label: 'User' },
  { value: 'portco', label: 'Portfolio Company User' },
];

export function UserForm({ open, onClose, user }: UserFormProps) {
  const currentUser = useAuthStore((state) => state.user);
  const updateUserProfile = useAuthStore((state) => state.updateUserProfile);
  const fetchUsers = useAuthStore((state) => state.fetchUsers);
  const isEditMode = !!user;
  const { data: picklists } = usePicklists();

  // Filter roles based on current user's role
  const availableRoles = currentUser?.role === 'super_admin' ? ALL_ROLES : ALL_ROLES.filter((r) => r.value !== 'super_admin');

  // Get user_function picklist options
  const functionOptions = useMemo(() => {
    const userFunctionPicklist = picklists?.find((p) => p.name === 'user_function');
    if (!userFunctionPicklist) return [];
    return (userFunctionPicklist.items as Array<{ id: string; label: string; description?: string; order: number }>)
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        value: item.id,
        label: item.label,
        description: item.description,
      }));
  }, [picklists]);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'user',
    user_function: '',
    organization: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFormData({
        email: user?.email || '',
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        role: user?.role || 'user',
        user_function: user?.user_function || '',
        organization: user?.organization || '',
      });
      setError(null);
    }
  }, [open, user]);

  const handleFieldChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, role: event.target.value });
  };

  const handleFunctionChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, user_function: event.target.value });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode) {
        // Update user profile
        await updateUserProfile(user!.id, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          full_name: `${formData.first_name} ${formData.last_name}`.trim() || null,
          organization: formData.organization,
        });

        // Update role if changed
        if (formData.role !== user!.role) {
          const roleResponse = await authenticatedFetch(`/users/${user!.id}/role`, {
            method: 'PATCH',
            body: JSON.stringify({ role: formData.role }),
          });
          if (!roleResponse.ok) {
            throw new Error('Failed to update role');
          }
        }

        // Update function if changed
        if (formData.user_function !== user!.user_function) {
          const functionResponse = await authenticatedFetch(`/users/${user!.id}/function`, {
            method: 'PATCH',
            body: JSON.stringify({ user_function: formData.user_function || null }),
          });
          if (!functionResponse.ok) {
            throw new Error('Failed to update function');
          }
        }
      } else {
        // Call API to invite user
        const redirectTo = `${window.location.origin}/auth/set-password`;

        const response = await authenticatedFetch('/users/invite', {
          method: 'POST',
          body: JSON.stringify({
            email: formData.email.trim().toLowerCase(),
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            portfolio_company_id: null,
            role: formData.role,
            user_function: formData.user_function || undefined,
            organization: formData.organization.trim() || undefined,
            redirect_to: redirectTo,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to invite user');
        }

        // Refresh users list
        if (currentUser?.tenant_id) {
          await fetchUsers(currentUser.tenant_id);
        }
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : isEditMode ? 'Failed to update user' : 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPending = isSubmitting;
  const title = isEditMode ? 'Edit User' : 'Create User';

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 450, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} strokeWidth={2} />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Email" type="email" value={formData.email} onChange={handleFieldChange('email')} disabled={isEditMode} required fullWidth size="small" />

          <TextField label="First Name" value={formData.first_name} onChange={handleFieldChange('first_name')} required fullWidth size="small" />

          <TextField label="Last Name" value={formData.last_name} onChange={handleFieldChange('last_name')} required fullWidth size="small" />

          <FormControl fullWidth size="small">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select labelId="role-select-label" id="role-select" value={formData.role} label="Role" onChange={handleRoleChange}>
              {availableRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="function-select-label">Function (Optional)</InputLabel>
            <Select labelId="function-select-label" id="function-select" value={formData.user_function} label="Function (Optional)" onChange={handleFunctionChange}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {functionOptions.map((func) => (
                <MenuItem key={func.value} value={func.value}>
                  <Box>
                    <Typography variant="body2">{func.label}</Typography>
                    {func.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {func.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Job function for policy and agent behavior customization</FormHelperText>
          </FormControl>

          <TextField label="Organization" value={formData.organization} onChange={handleFieldChange('organization')} fullWidth size="small" />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={onClose} fullWidth disabled={isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} fullWidth disabled={isPending} startIcon={isPending ? <CircularProgress size={16} /> : undefined}>
            {isPending ? 'Saving...' : isEditMode ? 'Save' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
